import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Col, DatePicker, Input, Row, Spin, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setMentenancePlanning,
  updatePropsRescheduled,
} from '../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setReschedulingRequestMentenancePlannings } from '../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import { RootState } from '../../../../store';
import MentenancePlanningModal from '../MentenancePlanningModal/MentenancePlanningModal';

const MaintenancePlanningReschedulingRequestsTab: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const reduxMentenancePlanning = useSelector((state: RootState) => state?.models?.mentenancePlanning);
  const reschedulingRequestMentenancePlannings = useSelector(
    (state: RootState) => state?.store?.mentenancePlanning?.reschedulingRequestMentenancePlanningData ?? []
  );

  const searchTerm = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsRescheduled?.searchTerm);
  const skip = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsRescheduled?.skip);
  const take = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsRescheduled?.take);
  const refresh = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsRescheduled?.refresh);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const currentPage =
    Math.floor(reduxMentenancePlanning?.propsRescheduled?.skip / reduxMentenancePlanning?.propsRescheduled?.take) + 1;
  const pageSizes = [10, 20, 30, 50];

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const { message } = App.useApp();

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updatePropsRescheduled({ field: 'searchTerm', value: termInUrl }));
    dispatch(updatePropsRescheduled({ field: 'skip', value: calculatedSkip }));
    dispatch(updatePropsRescheduled({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    refetch,
    isFetching: isMentenancePlanningLoading,
  } = useQuery({
    queryKey: ['mentenancePlannings', searchTerm, skip, take, refresh, dateRange],
    queryFn: () =>
      MentenancePlanningService.searchRecheduled({
        statusIds: [5],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
      }).then((data) => {
        dispatch(updatePropsRescheduled({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setReschedulingRequestMentenancePlannings(data?.response));
        dispatch(updatePropsRescheduled({ field: 'refresh', value: false }));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      //refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updatePropsRescheduled({ field: 'searchTerm', value }));
      dispatch(updatePropsRescheduled({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates || [null, null]);
    dispatch(updatePropsRescheduled({ field: 'skip', value: 0 }));
  };

  const handleViewInfo = (record: any) => {
    setSelectedRecord(record);
    dispatch(setMentenancePlanning(record));
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleDeleteRecord = async (record: any) => {
    try {
      const response = await MentenancePlanningService.delete(record.id);
      if (response?.errors?.length) {
        // Handle errors if any
        console.error('Failed to delete record:', response.errors);
      } else {
        // Refresh the data after deletion
        message.success(`Mentenanta ${record.number} a fost stearsa`);
        dispatch(updatePropsRescheduled({ field: 'refresh', value: true }));
      }
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const baseColumns: any = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag> },
    {
      title: 'User',
      dataIndex: '',
      render: (record: any) => (
        <div>
          <div>{record?.registeredByUser?.name}</div>
          <div>{record.createDate ? moment(record.createDate).format('DD-MM-YYYY') : '-'}</div>
        </div>
      ),
    },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
    },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>
          {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
            <span>
              {mentenancePlanningSystemTypes.length > 0
                ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                : ''}
            </span>
          )}
        </>
      ),
    },
    {
      title: 'Perioada programata',
      dataIndex: '',
      render: (_value: any, record: any, _index: any) => {
        return (
          <>
            <span>{record.appointment ? moment(record.appointment).format('DD-MM-YYYY') : '-'}</span>
          </>
        );
      },
    },

    { title: 'Status', dataIndex: ['status', 'name'] },
    { title: 'Tehnician', dataIndex: ['assignedTechnician', 'name'] },
    {
      title: 'Actiuni',
      dataIndex: 'actions',
      key: 'actions',
      render: (_text: any, record: any) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '80px' }}>
            <Button onClick={() => handleViewInfo(record)} icon={<EyeOutlined />}></Button>
            <Button onClick={() => handleDeleteRecord(record)} icon={<DeleteOutlined />}></Button>
          </div>
        );
      },
    },
  ];

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxMentenancePlanning?.propsRescheduled.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updatePropsRescheduled({ field: 'skip', value: skipValue }));
      dispatch(updatePropsRescheduled({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updatePropsRescheduled({ field: 'skip', value: 0 }));
      dispatch(updatePropsRescheduled({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };
  if (isMentenancePlanningLoading) {
    return <Spin className='spinner' size='large'></Spin>;
  }
  return (
    <div className='table-page-no-h-scroll'>
      <h3>Cereri Reprogramare</h3>

      <Row gutter={16} style={{ marginBottom: '16px', rowGap: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder='Cauta...'
            defaultValue={searchTerm}
            onChange={(e: any) => handleSearch(e.target.value)}
            allowClear
            style={{ width: '100%' }}
            autoFocus={true}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <DatePicker.RangePicker
            value={[dateRange[0], dateRange[1]]}
            onChange={handleDateRangeChange}
            placeholder={['Data inceput', 'Data sfarsit']}
            allowClear
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <div className='table-scroll-wrapper'>
        <Table
          columns={baseColumns}
          dataSource={reschedulingRequestMentenancePlannings}
          pagination={paginationConfig}
          rowKey='number'
          scroll={{ x: 'max-content' }}
        />
      </div>

      <MentenancePlanningModal
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        selectedRecord={selectedRecord}
        onUpdate={() => refetch()}
      />
    </div>
  );
};

export default MaintenancePlanningReschedulingRequestsTab;
